import { useCallback, useEffect, useMemo } from 'react'
import { atom, useAtom } from 'jotai'
import { useApiClient } from '../../../index'
import {
  refreshedTypes,
  viewStateStore,
  viewTypesByNameAtom,
} from '../../store'
import type { IViewType } from '../../../index'

export const useViewTypeByNameAtom = (viewType: string) => {
  const apiClient = useApiClient()?.viewState

  const [get, set] = useAtom(viewTypesByNameAtom, { store: viewStateStore })

  // Initialise the current scope in the global atom.
  const viewTypeAtom = useMemo(() => {
    if (viewType in get) {
      return get[viewType]
    }
    const viewTypeAtom = atom<IViewType | undefined>(undefined)

    viewTypeAtom.debugLabel = `viewtype.${viewType}`

    // Add atoms to global atom.
    set((prev) => {
      return {
        ...prev,
        [viewType]: viewTypeAtom,
      }
    })
    return viewTypeAtom
  }, [viewType, get, set])

  const [viewTypeValue, setViewTypeValue] = useAtom(viewTypeAtom, {
    store: viewStateStore,
  })

  const refreshViewType = useCallback(async () => {
    if (!apiClient) return
    try {
      const res = await apiClient.listViewTypes({
        name: viewType,
      })
      setViewTypeValue(res.viewTypes.length > 0 ? res.viewTypes[0] : undefined)
    } catch (error) {
      throw new Error('Failed to load list view types')
    }
  }, [viewType])

  // Always populate view states on mount.
  useEffect(() => {
    ;(async () => {
      if (refreshedTypes.includes(viewType)) return
      refreshedTypes.push(viewType)
      await refreshViewType()
    })()
  }, [])

  return {
    viewType: viewTypeValue,
    setViewType: setViewTypeValue,
    refreshViewType: refreshViewType,
  }
}
